import type { MechanicDefinition } from "./types";

export const research: MechanicDefinition = {
  id: "research",
  disabled: true,
  name: "Research Tree",
  description: "Tech tree / research system — unlock upgrades, abilities, and advancements through prerequisite research",
  icon: "FlaskConical",
  category: "systems",
  aiHints: {
    auditPasses: 0,
    purpose: "Players research technologies, spells, or advancements in a prerequisite tree — unlocking upgrades, buildings, units, and abilities.",
    whenToUse: "Strategy, RTS, 4X, or tycoon games where progression happens through a tech/research tree rather than just leveling up.",
    synergies: ["resources", "organization", "territory", "economy"],
    conflicts: [],
    prerequisites: [],
    tags: ["research", "tech_tree", "strategy", "progression", "upgrades", "science", "RTS"],
    complexity: 4,
    exampleConfig: { research_speed: "standard", max_concurrent: 1, research_categories: "military,economy,infrastructure,special" },
    stateVariables: ["_research_completed", "_research_in_progress", "_research_queue", "_research_points"],
  },
  configSchema: [
    {
      key: "research_categories",
      label: "Research Categories",
      type: "text",
      default: "military,economy,infrastructure,special",
      description: "Comma-separated categories for your research tree (e.g. military,economy,magic,infrastructure)",
    },
    {
      key: "research_speed",
      label: "Research Speed",
      type: "select",
      options: [
        { value: "instant", label: "Instant", description: "Research completes immediately when started" },
        { value: "fast", label: "Fast", description: "1-2 turns per research" },
        { value: "standard", label: "Standard", description: "3-5 turns per research" },
        { value: "slow", label: "Slow", description: "5-10 turns, more strategic pacing" },
      ],
      default: "standard",
    },
    {
      key: "max_concurrent",
      label: "Max Concurrent Research",
      type: "select",
      options: [
        { value: "1", label: "1 — Focused", description: "Only one research at a time — forces prioritization" },
        { value: "2", label: "2 — Moderate", description: "Two parallel research tracks" },
        { value: "3", label: "3 — Advanced", description: "Three parallel tracks — complex strategy" },
      ],
      default: "1",
    },
    {
      key: "research_currency",
      label: "Research Currency",
      type: "text",
      default: "Research Points",
      description: "Name for the research resource (e.g. Research Points, Science, Mana Knowledge)",
      placeholder: "Research Points",
    },
    {
      key: "passive_generation",
      label: "Passive Research Generation",
      type: "boolean",
      default: true,
      description: "Generate research points passively each turn based on infrastructure and personnel",
    },
    {
      key: "prerequisite_depth",
      label: "Tree Depth",
      type: "select",
      options: [
        { value: "shallow", label: "Shallow (2-3 tiers)", description: "Quick progression, few prerequisites" },
        { value: "medium", label: "Medium (4-5 tiers)", description: "Balanced depth with meaningful choices" },
        { value: "deep", label: "Deep (6+ tiers)", description: "Complex tech tree with many branching paths" },
      ],
      default: "medium",
    },
  ],
  systemPromptSection: (config) => {
    const categories = (config.research_categories || "military,economy,infrastructure,special")
      .split(",").map((s: string) => s.trim()).filter(Boolean);
    const speed = config.research_speed || "standard";
    const maxConcurrent = parseInt(config.max_concurrent || "1", 10);
    const currency = config.research_currency || "Research Points";
    const passive = config.passive_generation !== false;
    const depth = config.prerequisite_depth || "medium";

    return `## RESEARCH TREE (Rules — Mandatory Post-Turn)
- Research categories: ${categories.join(", ")}
- Research currency: ${currency}
- Speed: ${speed === "instant" ? "Instant completion" : speed === "fast" ? "1-2 turns" : speed === "slow" ? "5-10 turns" : "3-5 turns"}
- Max concurrent research: ${maxConcurrent}
- Tree depth: ${depth}

### RESEARCH ENTITIES
- Each research topic is an ENTITY of type "research" in the world.
- Research entities MUST define in properties:
  - tier (number 1-6): determines position in tree
  - prerequisites (string[]): names/IDs of required completed research
  - cost (number): ${currency} required to complete
  - turns_required (number): turns to complete
  - category (string): one of [${categories.join(", ")}]
  - effects (string): what this research unlocks
  - unlocks (string[]): what becomes available after completion

### RULES
- Use start_research to begin. Check prerequisites are met + player has enough ${currency}.
- Use research_status for full tree overview with completed/in-progress/locked status.
- Use complete_research when turns_remaining reaches 0.
- Use cancel_research to abort (refunds 50% of spent ${currency}).
- NEVER allow research without meeting ALL prerequisites.
- Research completion can unlock: new units, buildings, abilities, mechanics, upgrades.
- When research completes, announce the unlock narratively.
${passive ? `
### PASSIVE GENERATION
- Generate ${currency} each turn based on:
  - Base: 5 per turn
  - Bonus from buildings/locations tagged as "research" producers
  - Bonus from NPCs assigned as researchers
- Track via _research_points_per_turn and add to _research_points each post-turn.` : ""}

### PREREQUISITE TREE LOGIC
- Tier 1 research has NO prerequisites (entry points).
- Higher tier research requires specific lower-tier completions.
- Some research may require research from MULTIPLE categories (cross-discipline).
- Display locked research with "???" if prerequisites not visible yet.

### NARRATIVE ACCESS REQUIREMENTS
- Research is NOT a menu. Players must narratively access research infrastructure.
- If world context specifies HOW research is accessed, enforce that path strictly.
- If no world context is provided, invent a fitting in-world access point (lab, library, terminal, mentor NPC, archive) that matches the setting.
- When player asks to "view tree" or "check research," narrate them approaching the access point — do NOT break the fourth wall with a raw data dump.
- Research options should be presented as in-world discoveries, not game UI lists. E.g. "You browse the terminal and see three available projects…"
- Some research may require narrative prerequisites beyond point costs (finding a blueprint, convincing an NPC, reaching a location).
- If the player lacks narrative access (not at the right location, hasn't met the right NPC), tell them what they need to do in-story to gain access.`;
  },
  widgets: [
    {
      id: "research_status_bar",
      type: "status_bar_item",
      label: "Research",
      icon: "FlaskConical",
      component: "ResearchStatusWidget",
      dataBindings: {
        completedCount: "variables._research_completed_count",
        inProgressCount: "variables._research_in_progress_count",
        researchPoints: "variables._research_points",
        currency: "variables._research_currency_name",
      },
      visibility: "when_data",
      priority: 8,
    },
    {
      id: "research_panel",
      type: "sidebar_tab",
      label: "Research",
      icon: "FlaskConical",
      component: "ResearchPanel",
      dataBindings: {
        completed: "variables._research_completed",
        inProgress: "variables._research_in_progress",
        queue: "variables._research_queue",
        researchPoints: "variables._research_points",
        pointsPerTurn: "variables._research_points_per_turn",
        currency: "variables._research_currency_name",
      },
      visibility: "always",
      priority: 39,
      actions: [
        { id: "research_tree", label: "View Tree", icon: "GitBranch", messageTemplate: "Show me the full research tree and my progress." },
        { id: "start_research", label: "Research", icon: "FlaskConical", messageTemplate: "I want to research {{research_name}}." },
        { id: "research_report", label: "Status", icon: "ClipboardList", messageTemplate: "Give me a research status report." },
      ],
    },
  ],
  initializeState: (config) => ({
    _research_completed: [],        // [{id, name, category, tier, completed_turn, effects}]
    _research_in_progress: [],      // [{id, name, category, tier, cost, turns_required, turns_remaining, started_turn}]
    _research_queue: [],            // [{id, name}] — queued for next slot
    _research_points: 0,
    _research_points_per_turn: config.passive_generation !== false ? 5 : 0,
    _research_currency_name: config.research_currency || "Research Points",
    _research_completed_count: 0,
    _research_in_progress_count: 0,
  }),
  postTurnHook: (context) => {
    const vars = context.state.variables || {};
    const inProgress = [...(vars._research_in_progress || [])];
    const completed = [...(vars._research_completed || [])];
    const passive = context.config.passive_generation !== false;
    const notes: string[] = [];
    const updates: Record<string, any> = {};

    // Passive generation
    if (passive) {
      const perTurn = vars._research_points_per_turn || 5;
      updates._research_points = (vars._research_points || 0) + perTurn;
      if (perTurn > 0) notes.push(`🔬 +${perTurn} ${vars._research_currency_name || "RP"} generated.`);
    }

    // Progress in-flight research
    const stillInProgress: any[] = [];
    for (const r of inProgress) {
      const remaining = (r.turns_remaining || 1) - 1;
      if (remaining <= 0) {
        completed.push({ ...r, completed_turn: vars._current_turn || 0 });
        notes.push(`✅ Research complete: "${r.name}" — ${r.effects || "new capability unlocked"}`);
      } else {
        stillInProgress.push({ ...r, turns_remaining: remaining });
        if (remaining === 1) notes.push(`🔬 "${r.name}" completes next turn!`);
      }
    }

    if (inProgress.length > 0 || passive) {
      updates._research_in_progress = stillInProgress;
      updates._research_completed = completed;
      updates._research_completed_count = completed.length;
      updates._research_in_progress_count = stillInProgress.length;
    }

    return {
      stateUpdates: Object.keys(updates).length > 0 ? updates : undefined,
      narrativeNotes: notes.length > 0 ? notes : undefined,
    };
  },
  onEvent: (event) => {
    if (event.type === "territory_claimed") {
      return {
        narrativeNotes: ["🔬 New territory may provide research bonuses — check for research facilities."],
      };
    }
    if (event.type === "quest_complete") {
      return {
        narrativeNotes: ["🔬 Quest completion may unlock special research topics."],
      };
    }
    return null;
  },
  tools: [
    {
      type: "function",
      function: {
        name: "start_research",
        description: "Begin researching a topic. Must have prerequisites completed and enough research points. Checks against research entities in the world.",
        parameters: {
          type: "object",
          properties: {
            research_id: { type: "string", description: "Entity ID of the research topic" },
            research_name: { type: "string", description: "Human-readable name" },
            category: { type: "string", description: "Research category" },
            tier: { type: "number", description: "Tier in the tech tree (1 = entry level)" },
            cost: { type: "number", description: "Research points required" },
            turns_required: { type: "number", description: "Turns to complete" },
            prerequisites: { type: "array", items: { type: "string" }, description: "Names of prerequisite research that must be completed" },
            effects: { type: "string", description: "What this research unlocks when completed" },
          },
          required: ["research_id", "research_name", "category", "tier", "cost", "turns_required"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "complete_research",
        description: "Mark a research as completed. Called when turns_remaining reaches 0 or for instant research.",
        parameters: {
          type: "object",
          properties: {
            research_id: { type: "string", description: "Entity ID" },
            research_name: { type: "string" },
            effects: { type: "string", description: "What was unlocked" },
            unlocks: { type: "array", items: { type: "string" }, description: "Research names now available" },
          },
          required: ["research_id", "research_name"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "cancel_research",
        description: "Cancel in-progress research. Refunds 50% of spent research points.",
        parameters: {
          type: "object",
          properties: {
            research_id: { type: "string", description: "Entity ID" },
            research_name: { type: "string" },
          },
          required: ["research_id", "research_name"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "research_status",
        description: "Full research tree overview: completed, in-progress, available, and locked research with prerequisites.",
        parameters: {
          type: "object",
          properties: {},
          required: [],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "adjust_research_points",
        description: "Add or remove research points (from buildings, events, loot, etc.)",
        parameters: {
          type: "object",
          properties: {
            amount: { type: "number", description: "Positive to add, negative to remove" },
            reason: { type: "string", description: "Why research points changed" },
          },
          required: ["amount", "reason"],
        },
      },
    },
  ],
};
